import { type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import type { FyndanuListing } from "@/lib/fyndanu.functions";
import { hashString, planKbBlocks, splitByH2 } from "@/lib/kb-blocks";
import { KbBlockView } from "@/components/kb/KbBlocks";

function h2Slug(children: ReactNode): string {
  const text = (Array.isArray(children) ? children : [children])
    .map((c) => (typeof c === "string" ? c : typeof c === "number" ? String(c) : ""))
    .join("")
    .trim();
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 80);
}

export const mdComponents = {
  h2: ({ children, ...props }: { children?: ReactNode }) => (
    <h2 id={h2Slug(children)} {...props}>
      {children}
    </h2>
  ),
  table: ({ children, ...props }: { children?: ReactNode }) => (
    <div className="not-prose my-8 -mx-4 md:mx-0 overflow-x-auto rounded-2xl border border-border bg-card/60">
      <table className="w-full min-w-[560px] border-collapse text-sm md:text-[15px]" {...props}>
        {children}
      </table>
    </div>
  ),
  thead: ({ children, ...props }: { children?: ReactNode }) => (
    <thead className="bg-secondary/60 text-left" {...props}>
      {children}
    </thead>
  ),
  th: ({ children, ...props }: { children?: ReactNode }) => (
    <th
      className="px-4 py-3 font-display text-xs uppercase tracking-wider text-foreground/80 border-b border-border"
      {...props}
    >
      {children}
    </th>
  ),
  td: ({ children, ...props }: { children?: ReactNode }) => (
    <td className="px-4 py-3 align-top border-b border-border/60 text-foreground/85" {...props}>
      {children}
    </td>
  ),
  img: ({ src, alt }: { src?: string; alt?: string }) => (
    <img
      src={src}
      alt={alt ?? ""}
      loading="lazy"
      className="my-8 w-full rounded-2xl border border-border object-cover"
    />
  ),
};

/**
 * Renderar artikeltexten och skjuter in våra egna block (prisjämförelse ur lagret,
 * interna nyckeltal, kalkylatorer, egen bild, Fyndanu-tips) mellan avsnitten där
 * de passar ämnet. Block visas bara när det finns riktiga data bakom dem.
 */
export function KbArticleBody({
  slug,
  keyword,
  content,
  listings,
}: {
  slug: string;
  keyword?: string | null;
  content: string;
  listings: FyndanuListing[];
}) {
  const segments = splitByH2(content);
  const blocks = planKbBlocks(slug, keyword ?? "", content);
  const seed = hashString(slug);

  // Segment 0 = intro före första H2 → block hör till avsnittsindex = segmentindex.
  const byIndex = new Map<number, typeof blocks>();
  for (const b of blocks) {
    const list = byIndex.get(b.afterSection) ?? [];
    list.push(b);
    byIndex.set(b.afterSection, list);
  }

  return (
    <div className="prose prose-neutral max-w-none dark:prose-invert prose-headings:font-display prose-a:text-accent prose-a:no-underline hover:prose-a:underline">
      {segments.map((seg, i) => (
        <div key={i}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]} components={mdComponents}>
            {seg}
          </ReactMarkdown>
          {(byIndex.get(i) ?? []).map((b, j) => (
            <KbBlockView key={`${b.kind}-${j}`} block={b} listings={listings} seed={seed + j} />
          ))}
        </div>
      ))}
    </div>
  );
}
